import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import FilePreviewer from '@components/FilePreviewer';
import ImageUpload from '@components/ImageUpload';
import LoaderComponent from '@components/LoaderComponent';
import NitexImageCropper from '@components/NitexImageCropper';
import { toastError, toastSuccess } from '@components/Toast';

import { fetchProductInfo, productWiseGroupDocument } from '@store/actions/DesignToolAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { whichUserUploadImageCheckForFabric } from '@utils/MaterialsUtil';
import { checkAnyUserType, getFileType } from '@utils/Util';

const UploadImage = ({
	document,
	title,
	documentGroup = 'PHYSICAL_SAMPLE',
	docType,
	removeImage,
	singleImageUpload
}) => {
	const [croppedBodyData, setCroppedBodyData] = useState({
		// id: '',
		base64Str: '',
		docMimeType: 'image/jpeg',
		extension: 'jpeg'
	});
	let loader = useSelector((store) => store.loaderStore);
	const [isEdit, setIsEdit] = useState(false);
	const { id } = useParams();
	const dispatch = useDispatch();

	const fetchUpdatedProductDesign = () => {
		if (docType === 'FRONT_IMAGE') dispatch(fetchProductInfo(id));
	};

	const onUploadImageForPresentation = async (image) => {
		if (validateFileSize(image.size)) {
			dispatch(openLoader());
			await ApiClient.POST('addNewGroupDocument', image)
				.then(({ data }) => {
					dispatch(productWiseGroupDocument(id));
					fetchUpdatedProductDesign();
					dispatch(closeLoader());
					toastSuccess(data.message);
				})
				.catch(({ response }) => {
					dispatch(closeLoader());
					toastError(response.data.message);
				});
		}
	};

	const getImageType = (type) => {
		switch (type) {
			case 'Art works':
				return 'allDocType';
			case 'References':
				return 'allDocType';
			case 'image':
				return 'image';
			default:
				return 'image';
		}
	};

	const onSuccess = (base64str) => {
		if (base64str?.base64Str) {
			toastSuccess('Updated successfully');
		} else {
			toastSuccess('Cropped successfully');
		}
	};

	const validateFileSize = (size) => {
		if (size > 1000000) {
			toastError('Please upload file less than 1mb');
			return false;
		} else {
			return true;
		}
	};

	const onCroppedDataSave = async (base64str) => {
		if (validateFileSize(base64str.size)) {
			if (base64str) {
				dispatch(openLoader());
				await ApiClient.POST(
					'updateGroupDocument',
					{
						...croppedBodyData,
						base64Str: !isEdit ? base64str?.base64Str : base64str,
						productId: parseInt(id),
						id: document?.id
					},
					id
				)
					.then(() => {
						dispatch(productWiseGroupDocument(id));
						fetchUpdatedProductDesign();
						dispatch(closeLoader());
						onSuccess(base64str);
					})
					.catch(() => {
						dispatch(closeLoader());
						onSuccess(base64str);
					});
			} else {
				onSuccess(base64str);
			}
			setIsEdit(false);
		}
	};

	const onEdit = (e, id) => {
		setIsEdit(true);
		setCroppedBodyData({ ...croppedBodyData, id: id });
	};

	const renderFileType = (docId, docUrl) => {
		let fileType = getFileType(docUrl);
		if (fileType === 'IMAGE') {
			return (
				<img
					onClick={(e) => onEdit(e, docId, docUrl)}
					src={`${process.env.PUBLIC_URL}/icons/crop.png`}
					className="crop"
					alt="edit"
				/>
			);
		}
	};

	const renderDeleteIcon = (docId, docUrl) => {
		if (docUrl) {
			return (
				<img
					onClick={() => removeImage(docId)}
					src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
					className="crop delete"
					alt="delete"
				/>
			);
		}
	};

	const renderImageForPresentations = () => {
		if (document?.docUrl) {
			return (
				<div className="uploaded-file">
					{renderFileType(document?.id, document?.docUrl)}
					{removeImage &&
						checkAnyUserType(['PHOTOGRAPHY', 'Admin', 'FASHION_DESIGNER']) &&
						renderDeleteIcon(document?.id, document?.docUrl)}
					<ImageUpload
						onUploadImage={onCroppedDataSave}
						documentGroup={documentGroup}
						docType={docType}
						productId={id}
						type={getImageType('image')}
						isEditable={true}
						removeImage={removeImage}
					/>
					<div className="uploaded-image">
						<FilePreviewer docUrl={document?.docUrl} base64Data={document?.base64Str} />
					</div>
				</div>
			);
		} else {
			return (
				<div className="file-uploader upload-design-image">
					<span className="drag-upload">{title}</span>
					<ImageUpload
						onUploadImage={onUploadImageForPresentation}
						documentGroup={documentGroup}
						docType={docType}
						productId={id}
						type={getImageType('image')}
					/>
				</div>
			);
		}
	};
	const renderImageForAnother = () => {
		if (document?.docUrl) {
			return (
				<div className="uploaded-file">
					{/*{renderFileType(document?.id, document?.docUrl)}*/}
					{removeImage &&
						whichUserUploadImageCheckForFabric() &&
						renderDeleteIcon(document?.id, document?.docUrl)}
					<ImageUpload
						onUploadImage={singleImageUpload}
						documentGroup={documentGroup}
						docType={docType}
						productId={id}
						type={getImageType('image')}
						isEditable={true}
						removeImage={removeImage}
					/>
					<div className="uploaded-image">
						<FilePreviewer docUrl={document?.docUrl} base64Data={document?.base64Str} />
					</div>
				</div>
			);
		} else {
			return (
				<div className="file-uploader upload-design-image">
					<span className="drag-upload">{title}</span>
					<ImageUpload
						onUploadImage={singleImageUpload}
						documentGroup={documentGroup}
						docType={docType}
						productId={id}
						type={getImageType('image')}
					/>
				</div>
			);
		}
	};

	return (
		<LoaderComponent loading={loader}>
			<div className="one-forth">
				{singleImageUpload === undefined || singleImageUpload === null
					? renderImageForPresentations()
					: renderImageForAnother()}
			</div>
			{isEdit && (
				<NitexImageCropper
					isEdit={isEdit}
					onClose={() => setIsEdit(false)}
					imageSrc={document?.docUrl}
					onSave={onCroppedDataSave}
				/>
			)}
		</LoaderComponent>
	);
};

export default UploadImage;
